import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brain, TrendUp, Robot, ArrowRight, MagnifyingGlass, X, ShoppingBag, UsersThree, MagicWand, Flask, CalendarBlank, Buildings, ShieldCheck, HandPointing, Gear } from '@phosphor-icons/react';
import { Container, Title, Text, Button, Paper, SimpleGrid, ThemeIcon, Modal, Stack, Badge, Group, List, HoverCard, Anchor } from '@mantine/core';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Only structural data lives here (id, icon, mock illustration). All user-facing
// text is externalised to i18n (namespace `features`). `mock` is a function of `t`
// so the tiny labels inside the illustrations can be translated too.
const FEATURES = [
  {
    id: 'optimization-247',
    icon: Brain,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#ffffff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.optimization-247.mock.title')}</text>
        <rect x="226" y="20" width="74" height="18" rx="9" fill="#1D4ED8" opacity="0.1"/>
        <text x="263" y="33" textAnchor="middle" fontSize="9" fill="#1D4ED8" fontWeight="600" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.optimization-247.mock.badge')}</text>
        <path d="M24 132 L80 112 L136 120 L192 86 L248 72 L300 50" stroke="#1D4ED8" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M24 132 L80 112 L136 120 L192 86 L248 72 L300 50 L300 152 L24 152 Z" fill="#1D4ED8" opacity="0.07"/>
        <circle cx="192" cy="86" r="4" fill="#1D4ED8"/>
        <circle cx="300" cy="50" r="4.5" fill="#1D4ED8"/>
        <line x1="24" y1="152" x2="300" y2="152" stroke="#e5e7eb" strokeWidth="1"/>
      </svg>
    ),
  },
  {
    id: 'conversions',
    icon: TrendUp,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f5f3ff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.conversions.mock.title')}</text>
        <text x="269" y="40" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1D4ED8" fontFamily="'IBM Plex Sans',sans-serif">+32%</text>
        {[{x:40,h:42,c:'#c4b5fd'},{x:92,h:58,c:'#c4b5fd'},{x:144,h:50,c:'#a5b4fc'},{x:196,h:78,c:'#60a5fa'},{x:252,h:104,c:'#1D4ED8'}].map((b,i)=>(
          <rect key={i} x={b.x} y={150-b.h} width="34" height={b.h} rx="5" fill={b.c}/>
        ))}
        <line x1="24" y1="150" x2="300" y2="150" stroke="#ddd6fe" strokeWidth="1"/>
      </svg>
    ),
  },
  {
    id: 'shopping-pla',
    icon: ShoppingBag,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f9fafb"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.shopping-pla.mock.title')}</text>
        {[{r:'4.2x',c:'#10b981',hl:true},{r:'2.6x',c:'#10b981',hl:false},{r:'0.0x',c:'#9ca3af',hl:false}].map((p,i)=>{
          const x = 20 + i * 100;
          return (
            <g key={i}>
              <rect x={x} y="48" width="80" height="96" rx="8" fill="#ffffff" stroke={p.hl ? '#1D4ED8' : '#e5e7eb'} strokeWidth={p.hl ? 1.5 : 1}/>
              <rect x={x + 10} y="58" width="60" height="44" rx="5" fill="#eef2ff"/>
              <circle cx={x + 40} cy="80" r="11" fill="#1D4ED8" opacity="0.18"/>
              <rect x={x + 10} y="110" width="44" height="7" rx="3.5" fill="#1e1b4b" opacity="0.13"/>
              <rect x={x + 10} y="122" width="36" height="15" rx="7.5" fill={p.c} opacity="0.15"/>
              <text x={x + 28} y="133" textAnchor="middle" fontSize="8.5" fontWeight="700" fill={p.c}>{p.r}</text>
            </g>
          );
        })}
        <circle cx="68" cy="48" r="7" fill="#1D4ED8"/>
        <text x="68" y="51" textAnchor="middle" fontSize="8" fill="#ffffff" fontWeight="700">★</text>
      </svg>
    ),
  },
  {
    id: 'meta-leads',
    icon: UsersThree,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#ffffff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.meta-leads.mock.title')}</text>
        <rect x="20" y="44" width="176" height="120" rx="10" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1"/>
        <rect x="34" y="60" width="148" height="16" rx="5" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1"/>
        <rect x="34" y="84" width="148" height="16" rx="5" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1"/>
        <rect x="34" y="108" width="148" height="16" rx="5" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1"/>
        <rect x="34" y="132" width="148" height="20" rx="6" fill="#1D4ED8"/>
        <text x="108" y="145" textAnchor="middle" fontSize="9" fill="#ffffff" fontWeight="600" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.meta-leads.mock.submit')}</text>
        <rect x="212" y="44" width="88" height="56" rx="9" fill="#eef2ff"/>
        <text x="224" y="62" fontSize="8" fill="#6b7280" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.meta-leads.mock.leadsLabel')}</text>
        <text x="224" y="84" fontSize="18" fontWeight="700" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">128</text>
        <text x="263" y="84" fontSize="9" fill="#10b981" fontWeight="600">+22%</text>
        <rect x="212" y="108" width="88" height="56" rx="9" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1"/>
        <text x="224" y="126" fontSize="8" fill="#6b7280" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.meta-leads.mock.costLabel')}</text>
        <text x="224" y="148" fontSize="16" fontWeight="700" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">4,10 zł</text>
      </svg>
    ),
  },
  {
    id: 'ai-creatives',
    icon: MagicWand,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f5f3ff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.ai-creatives.mock.title')}</text>
        <rect x="20" y="44" width="118" height="118" rx="10" fill="#ffffff" stroke="#ddd6fe" strokeWidth="1"/>
        <rect x="32" y="56" width="94" height="68" rx="6" fill="#eef2ff"/>
        <path d="M79 74 l4.5 11 l11 4.5 l-11 4.5 l-4.5 11 l-4.5 -11 l-11 -4.5 l11 -4.5 Z" fill="#1D4ED8"/>
        <rect x="32" y="132" width="74" height="9" rx="4.5" fill="#1D4ED8" opacity="0.2"/>
        <rect x="32" y="147" width="52" height="7" rx="3.5" fill="#1D4ED8" opacity="0.13"/>
        {['','',''].map((_, i) => (
          <g key={i}>
            <rect x="154" y={48 + i * 28} width="150" height="22" rx="6" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1"/>
            <circle cx="166" cy={59 + i * 28} r="3.5" fill="#1D4ED8"/>
            <rect x="176" y={55 + i * 28} width={i === 1 ? 110 : 86} height="8" rx="4" fill="#1e1b4b" opacity="0.14"/>
          </g>
        ))}
        <rect x="154" y="132" width="150" height="30" rx="6" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1"/>
        <rect x="166" y="142" width="120" height="6" rx="3" fill="#1e1b4b" opacity="0.1"/>
        <rect x="166" y="152" width="80" height="6" rx="3" fill="#1e1b4b" opacity="0.1"/>
      </svg>
    ),
  },
  {
    id: 'test-shops',
    icon: Flask,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f0fdfa"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.test-shops.mock.title')}</text>
        <circle cx="160" cy="94" r="40" fill="none" stroke="#ccfbf1" strokeWidth="11"/>
        <path d="M160 54 a40 40 0 1 1 -38 28" fill="none" stroke="#10b981" strokeWidth="11" strokeLinecap="round"/>
        <path d="M145 94 l10 10 l21 -23" fill="none" stroke="#10b981" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="160" y="160" textAnchor="middle" fontSize="11" fontWeight="600" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.test-shops.mock.caption')}</text>
      </svg>
    ),
  },
  {
    id: 'reports',
    icon: Robot,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f9fafb"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.reports.mock.title')}</text>
        <g transform="rotate(-90 96 104)">
          <circle cx="96" cy="104" r="34" fill="none" stroke="#1D4ED8" strokeWidth="16" strokeDasharray="107 214"/>
          <circle cx="96" cy="104" r="34" fill="none" stroke="#a5b4fc" strokeWidth="16" strokeDasharray="64 214" strokeDashoffset="-107"/>
          <circle cx="96" cy="104" r="34" fill="none" stroke="#ddd6fe" strokeWidth="16" strokeDasharray="43 214" strokeDashoffset="-171"/>
        </g>
        {[{c:'#1D4ED8',l:'Search',v:'52%'},{c:'#a5b4fc',l:'Shopping',v:'30%'},{c:'#ddd6fe',l:'Meta',v:'18%'}].map((s,i)=>(
          <g key={i}>
            <circle cx="178" cy={72 + i * 30} r="5" fill={s.c}/>
            <text x="192" y={76 + i * 30} fontSize="11" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">{s.l}</text>
            <text x="296" y={76 + i * 30} textAnchor="end" fontSize="11" fontWeight="600" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">{s.v}</text>
          </g>
        ))}
      </svg>
    ),
  },
  {
    id: 'scheduler',
    icon: CalendarBlank,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#ffffff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.scheduler.mock.title')}</text>
        <rect x="232" y="20" width="68" height="18" rx="9" fill="#1D4ED8" opacity="0.1"/>
        <text x="266" y="33" textAnchor="middle" fontSize="9" fill="#1D4ED8" fontWeight="600" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.scheduler.mock.badge')}</text>
        {Array.from({length:15}).map((_, i) => {
          const c = i % 5, r = Math.floor(i / 5);
          const x = 20 + c * 58, y = 46 + r * 36; const hl = i === 8;
          return <rect key={i} x={x} y={y} width="50" height="28" rx="5" fill={hl ? '#1D4ED8' : '#f3f4f6'}/>;
        })}
        <circle cx="199" cy="96" r="3.5" fill="#ffffff"/>
        <rect x="20" y="156" width="284" height="18" rx="6" fill="#eef2ff"/>
        <circle cx="33" cy="165" r="3" fill="#1D4ED8"/>
        <text x="42" y="168" fontSize="9" fill="#1D4ED8" fontWeight="600" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.scheduler.mock.event')}</text>
      </svg>
    ),
  },
  {
    id: 'agency',
    icon: Buildings,
    mock: (t) => (
      <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="320" height="180" rx="8" fill="#f5f3ff"/>
        <text x="20" y="32" fontSize="12" fontFamily="'IBM Plex Sans',sans-serif" fontWeight="600" fill="#1e1b4b">{t('features.items.agency.mock.title')}</text>
        {[{n:t('features.items.agency.mock.client1'),c:'#1D4ED8'},{n:t('features.items.agency.mock.client2'),c:'#10b981'},{n:t('features.items.agency.mock.client3'),c:'#f59e0b'}].map((cl,i)=>{
          const y = 44 + i * 38;
          return (
            <g key={i}>
              <rect x="20" y={y} width="280" height="30" rx="8" fill="#ffffff" stroke="#ede9fe" strokeWidth="1"/>
              <circle cx="38" cy={y + 15} r="10" fill={cl.c} opacity="0.16"/>
              <text x="38" y={y + 18} textAnchor="middle" fontSize="9" fontWeight="700" fill={cl.c}>{cl.n[0]}</text>
              <text x="56" y={y + 18} fontSize="10" fontWeight="600" fill="#1e1b4b" fontFamily="'IBM Plex Sans',sans-serif">{cl.n}</text>
              <circle cx="238" cy={y + 15} r="3" fill="#10b981"/>
              <text x="248" y={y + 18} fontSize="8.5" fill="#6b7280" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.agency.mock.status')}</text>
            </g>
          );
        })}
        <text x="20" y="170" fontSize="9" fill="#1D4ED8" fontWeight="500" fontFamily="'IBM Plex Sans',sans-serif">{t('features.items.agency.mock.footer')}</text>
      </svg>
    ),
  },
];

function FeatureCard({ feature, onOpen }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const Icon = feature.icon;
  const base = `features.items.${feature.id}`;

  return (
    <Paper
      radius="lg" p={0} withBorder
      className="relative hover:shadow-md hover:border-indigo-200 transition-all overflow-hidden cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(feature.id)}
    >
      {/* Mock screenshot */}
      <div className="bg-gray-50 border-b border-gray-100" style={{ padding: '16px 16px 0' }}>
        <div className="rounded-t-lg overflow-hidden border border-gray-200 border-b-0 bg-white shadow-sm">
          {/* Browser bar */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
            <div className="w-2 h-2 rounded-full bg-red-300" />
            <div className="w-2 h-2 rounded-full bg-yellow-300" />
            <div className="w-2 h-2 rounded-full bg-green-300" />
            <div className="flex-1 mx-2 h-3.5 bg-gray-100 rounded-sm" />
          </div>
          <div className="p-1">
            {feature.mock(t)}
          </div>
        </div>
      </div>

      {/* Text content */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2">
          <ThemeIcon size={40} radius="md" variant="light" color="brand">
            <Icon size={22} weight="duotone" />
          </ThemeIcon>
          <Title order={4} lh={1.2}>{t(`${base}.title`)}</Title>
        </div>
        <Text c="dimmed" size="sm" lh={1.6} mb="sm">{t(`${base}.desc`)}</Text>
        {/* Mobile button - always visible */}
        <button
          onClick={(e) => { e.stopPropagation(); onOpen(feature.id); }}
          className="md:hidden flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition"
        >
          <MagnifyingGlass size={14} weight="bold" />
          {t('features.moreDetails')}
        </button>
      </div>

      {/* Hover overlay - whole card darkens, desktop only */}
      <div
        className="absolute inset-0 hidden md:flex items-center justify-center transition-opacity duration-200"
        style={{
          opacity: hovered ? 1 : 0,
          background: 'rgba(80,70,229,0.06)',
        }}
      >
        <div className="bg-white shadow-md rounded-full px-4 py-2 flex items-center gap-2 border border-indigo-100">
          <MagnifyingGlass size={14} weight="bold" className="text-indigo-600" />
          <span className="text-sm font-medium text-indigo-700">{t('features.moreDetails')}</span>
        </div>
      </div>
    </Paper>
  );
}

function FeatureModal({ feature, onClose }) {
  const { t } = useTranslation();
  if (!feature) return null;
  const Icon = feature.icon;
  const base = `features.items.${feature.id}`;
  const bullets = t(`${base}.details.bullets`, { returnObjects: true });
  const bulletList = Array.isArray(bullets) ? bullets : [];

  return (
    <Modal
      opened={!!feature}
      onClose={onClose}
      size="lg"
      centered
      fullScreen={typeof window !== 'undefined' && window.innerWidth < 768}
      withCloseButton={false}
      padding={typeof window !== 'undefined' && window.innerWidth < 768 ? 'md' : 'lg'}
      styles={{
        body: { padding: typeof window !== 'undefined' && window.innerWidth < 768 ? '0 16px 16px' : undefined },
      }}
    >
      {/* Custom header with close button */}
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pt-4 pb-2 z-10 md:pt-0 md:static">
        <Group gap="sm">
          <ThemeIcon size={36} radius="md" variant="light" color="brand">
            <Icon size={20} weight="duotone" />
          </ThemeIcon>
          <Text fw={600} size="lg">{t(`${base}.title`)}</Text>
        </Group>
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
        >
          <X size={20} weight="bold" />
        </button>
      </div>

      <Stack gap="md">
        {/* Mock screenshot in modal */}
        <Paper bg="gray.0" p="sm" radius="md" withBorder>
          <div className="max-w-md mx-auto">
            {feature.mock(t)}
          </div>
        </Paper>

        <div>
          <Title order={3} mb="xs" className="md:text-lg">{t(`${base}.details.heading`)}</Title>
          <Text size="md" c="dimmed" lh={1.7} mb="md" className="md:text-sm">{t(`${base}.details.body`)}</Text>

          <List size="md" spacing="sm" c="dimmed" className="md:text-sm" icon={
            <ThemeIcon size={20} radius="xl" color="brand" variant="light">
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </ThemeIcon>
          }>
            {bulletList.map((b, i) => (
              <List.Item key={i}>{b}</List.Item>
            ))}
          </List>
        </div>

        <div className="flex flex-col-reverse md:flex-row md:justify-between gap-2 pb-4 md:pb-0">
          <Button variant="subtle" color="gray" size="md" onClick={onClose} className="md:hidden" fullWidth>{t('features.close')}</Button>
          <Button variant="subtle" color="gray" size="sm" onClick={onClose} className="hidden md:block">{t('features.close')}</Button>
          <Button component={Link} to="/rejestracja" color="brand" size="md" rightSection={<ArrowRight size={14} />} fullWidth className="md:w-auto md:!flex-none">
            {t('features.tryFree')}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

export default function FeaturesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFeatureId = searchParams.get('feature');
  const activeFeature = FEATURES.find(f => f.id === activeFeatureId) || null;

  const openFeature = (id) => {
    setSearchParams({ feature: id }, { replace: false });
  };

  const closeFeature = () => {
    setSearchParams({}, { replace: true });
  };

  const steps = t('features.howItWorks.steps', { returnObjects: true });
  const stepList = Array.isArray(steps) ? steps : [];

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO title={t('features.seo.title')} description={t('features.seo.description')} path="/funkcje" />
      <Navbar />

      <section className="bg-[#F6F5F4] py-24">
        <Container size="xl">
          <Title order={1} ta="center" mb="xs" fz={{ base: 30, md: 48 }}>
            {t('features.hero.title')}
          </Title>
          <Text c="dimmed" ta="center" mb={48} maw={640} mx="auto">
            {t('features.hero.lead1')}{' '}
            <HoverCard width={300} shadow="md" position="bottom" withArrow openDelay={100}>
              <HoverCard.Target>
                <span className="underline decoration-dotted decoration-indigo-400 underline-offset-4 cursor-help text-indigo-600 font-medium">
                  {t('features.hero.autonomously')}
                </span>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Text size="sm" fw={600} mb={4}>{t('features.hero.hoverTitle')}</Text>
                <Text size="xs" c="dimmed" lh={1.6}>
                  {t('features.hero.hoverBody')}
                </Text>
                <Anchor href="#control-levels" size="xs" mt="xs" style={{ display: 'inline-block' }}>
                  {t('features.hero.hoverLink')}
                </Anchor>
              </HoverCard.Dropdown>
            </HoverCard>
            {' '}{t('features.hero.lead2')}
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {FEATURES.map(feature => (
              <FeatureCard key={feature.id} feature={feature} onOpen={openFeature} />
            ))}
          </SimpleGrid>

          {/* Control levels card */}
          <Paper id="control-levels" radius="lg" withBorder className="overflow-hidden mt-12 relative scroll-mt-24">
            {/* Watermark logo */}
            <svg viewBox="0 0 859 772" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -top-8 -right-8 w-[280px] h-[280px] opacity-[0.04] pointer-events-none">
              <path d="M292.954 769.898C313.514 771.081 341.416 770.755 379.225 770.755H379.227C477.706 770.429 546.405 764.073 601.91 746.364C657.435 728.65 699.726 699.585 745.413 653.909C823.762 575.58 859.028 489.393 857.723 377.774C855.764 215.876 755.201 77.4812 600.462 21.3447L600.459 21.3438C577.614 13.1869 563.714 8.93507 537.655 6.40332C511.628 3.87462 473.454 3.06153 402.058 1.75781L402.059 1.75684C331.315 0.453137 293.63 -0.0368802 272.248 1.26855C261.559 1.92119 254.898 3.02406 250.202 4.71973C245.479 6.42564 242.766 8.72392 239.962 11.6924L239.956 11.6982L239.95 11.7051C234.953 17.3676 232.168 24.3443 230.616 41.8994C229.065 59.4474 228.739 87.6697 228.739 135.906C228.739 185.444 229.065 213.507 230.698 230.324C231.515 238.734 232.662 244.371 234.276 248.489C235.899 252.628 237.988 255.219 240.648 257.546C246.006 262.56 254.003 265.33 275.748 266.877C297.534 268.427 333.258 268.752 394.224 268.752H535.231V528.533H294.95V427.98C294.95 381.708 294.789 356.408 293.154 341.867C292.337 334.596 291.146 329.954 289.388 326.71C287.615 323.439 285.285 321.628 282.303 319.972L282.294 319.966L282.284 319.961C281.238 319.438 279.464 318.96 277.079 318.514C274.679 318.065 271.611 317.64 267.941 317.242C260.601 316.446 250.829 315.753 239.127 315.183C215.722 314.041 184.575 313.389 149.682 313.389C100.776 313.389 70.1073 313.878 50.1953 315.428C30.3037 316.976 21.0475 319.588 15.0557 323.914C11.6239 326.365 8.93761 328.494 6.89355 332.058C4.85631 335.609 3.4811 340.538 2.54102 348.547C0.66185 364.557 0.500001 393.119 0.5 448.19C0.5 481.445 1.15219 511.282 2.29395 533.785C2.86477 545.036 3.55815 554.459 4.35449 561.563C4.75263 565.115 5.17778 568.094 5.62695 570.435C6.07337 572.76 6.55187 574.5 7.07422 575.545L7.08398 575.564C8.74226 578.549 10.5571 580.879 13.9863 582.651C17.3839 584.407 22.3312 585.597 30.2119 586.413C45.9767 588.046 73.7253 588.208 124.901 588.208H235.261V668.551C235.261 695.254 234.77 715.021 236.404 729.604C238.04 744.202 241.818 753.751 250.469 759.905C259.075 766.027 272.414 768.717 292.954 769.898ZM294.95 588.208H436.611C487.476 588.208 523.517 587.719 547.577 586.496C559.606 585.885 568.654 585.089 575.069 584.076C578.276 583.57 580.839 583.008 582.792 582.382C584.731 581.761 586.13 581.059 586.95 580.238C587.385 579.803 587.75 579.037 588.077 578.061C588.412 577.062 588.731 575.768 589.036 574.205C589.647 571.079 590.211 566.831 590.731 561.632C591.772 551.23 592.639 536.972 593.332 520.15C594.718 486.505 595.411 442.572 595.411 398.643C595.411 354.713 594.718 310.781 593.332 277.135C592.639 260.313 591.772 246.056 590.731 235.653C590.211 230.454 589.647 226.207 589.036 223.08C588.731 221.518 588.412 220.223 588.077 219.225C587.75 218.248 587.385 217.482 586.95 217.047C586.129 216.226 584.718 215.523 582.757 214.902C580.781 214.277 578.182 213.714 574.925 213.208C568.408 212.196 559.197 211.401 546.924 210.789C522.374 209.566 485.519 209.077 433.351 209.077H288.43V60.1279H398.788C429.103 60.1279 460.56 60.9426 486.799 62.2461C513.052 63.5503 534.043 65.3424 543.455 67.2891L544.949 67.6055C671.863 94.825 774.035 203.203 796.736 335.488C825.381 504.716 701.019 676.575 529.132 703.914H529.129C505.696 707.819 443.781 711.079 390.963 711.079H294.95V588.208ZM60.1904 373.064H235.261V528.533H60.1904V373.064Z" fill="#1D4ED8"/>
            </svg>
            <div className="flex flex-col lg:flex-row">
              {/* Left: pseudo screenshot */}
              <div className="lg:w-[480px] shrink-0 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 p-6">
                <div className="rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
                  {/* Browser bar */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                    <div className="w-2 h-2 rounded-full bg-red-300" />
                    <div className="w-2 h-2 rounded-full bg-yellow-300" />
                    <div className="w-2 h-2 rounded-full bg-green-300" />
                    <div className="flex-1 mx-2 h-3.5 bg-gray-100 rounded-sm" />
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-[11px] font-bold text-gray-900 mb-1">{t('features.controlLevels.screenTitle')}</p>

                    {/* Mode 1: Manual - off */}
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-sm shrink-0">👁</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-900 leading-tight">{t('features.controlLevels.suggestionsTitle')}</p>
                        <p className="text-[9px] text-gray-500 leading-tight mt-0.5">{t('features.controlLevels.suggestionsScreenSub')}</p>
                      </div>
                      <div className="w-10 h-5 rounded-full bg-gray-200 relative shrink-0">
                        <div className="w-4 h-4 rounded-full bg-white shadow absolute top-0.5 left-0.5" />
                      </div>
                    </div>

                    {/* Mode 2: Safe auto - on */}
                    <div className="flex items-center gap-3 rounded-lg border-2 border-indigo-300 bg-indigo-50 px-3 py-2.5">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-sm shrink-0">⚡</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-[11px] font-semibold text-gray-900 leading-tight">{t('features.controlLevels.safeTitle')}</p>
                          <span className="text-[7px] font-bold text-indigo-600 bg-indigo-100 rounded px-1 py-0.5 uppercase leading-none">{t('features.controlLevels.safeBadge')}</span>
                        </div>
                        <p className="text-[9px] text-gray-500 leading-tight mt-0.5">{t('features.controlLevels.safeScreenSub')}</p>
                      </div>
                      <div className="w-10 h-5 rounded-full bg-indigo-600 relative shrink-0">
                        <div className="w-4 h-4 rounded-full bg-white shadow absolute top-0.5 right-0.5" />
                      </div>
                    </div>

                    {/* Mode 3: Full auto - off */}
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                      <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center text-sm shrink-0">🚀</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-gray-900 leading-tight">{t('features.controlLevels.fullTitle')}</p>
                        <p className="text-[9px] text-gray-500 leading-tight mt-0.5">{t('features.controlLevels.fullScreenSub')}</p>
                      </div>
                      <div className="w-10 h-5 rounded-full bg-gray-200 relative shrink-0">
                        <div className="w-4 h-4 rounded-full bg-white shadow absolute top-0.5 left-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: content */}
              <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center">
                <Badge variant="light" color="brand" size="lg" radius="sm" mb="md">
                  {t('features.controlLevels.badge')}
                </Badge>
                <Title order={2} mb="sm" fz={{ base: 24, md: 30 }} lh={1.2}>
                  {t('features.controlLevels.title')}
                </Title>
                <Text c="dimmed" size="md" lh={1.7} mb="lg">
                  {t('features.controlLevels.body')}
                </Text>

                <div className="space-y-4">
                  <div className="flex gap-3">
                    <ThemeIcon size={36} radius="md" variant="light" color="gray">
                      <HandPointing size={20} weight="duotone" />
                    </ThemeIcon>
                    <div>
                      <Text fw={600} size="sm">{t('features.controlLevels.suggestionsTitle')}</Text>
                      <Text c="dimmed" size="xs" lh={1.5}>{t('features.controlLevels.suggestionsDesc')}</Text>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <ThemeIcon size={36} radius="md" variant="light" color="brand">
                      <ShieldCheck size={20} weight="duotone" />
                    </ThemeIcon>
                    <div>
                      <Text fw={600} size="sm">{t('features.controlLevels.safeTitle')}</Text>
                      <Text c="dimmed" size="xs" lh={1.5}>{t('features.controlLevels.safeDesc')}</Text>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <ThemeIcon size={36} radius="md" variant="light" color="yellow">
                      <Gear size={20} weight="duotone" />
                    </ThemeIcon>
                    <div>
                      <Text fw={600} size="sm">{t('features.controlLevels.fullTitle')}</Text>
                      <Text c="dimmed" size="xs" lh={1.5}>{t('features.controlLevels.fullDesc')}</Text>
                    </div>
                  </div>
                </div>

                <Button component={Link} to="/rejestracja" color="brand" size="md" radius="md" mt="xl" rightSection={<ArrowRight size={16} />} className="self-start">
                  {t('features.controlLevels.cta')}
                </Button>
              </div>
            </div>
          </Paper>
        </Container>
      </section>

      {/* How it works */}
      <section className="bg-[#F6F5F4] py-24">
        <Container size="xl">
          <Title order={2} ta="center" mb="xs" fz={{ base: 30, md: 36 }}>
            {t('features.howItWorks.title')}
          </Title>
          <Text c="dimmed" ta="center" mb={48} maw={640} mx="auto">
            {t('features.howItWorks.lead')}
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg" maw={1000} mx="auto">
            {stepList.map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#1D4ED8] flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  {i + 1}
                </div>
                <Title order={4} mb="xs">{item.title}</Title>
                <Text c="dimmed" size="sm" lh={1.7}>{item.desc}</Text>
              </div>
            ))}
          </SimpleGrid>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-[#1D4ED8] py-20">
        <Container size="md" ta="center">
          <Title order={2} c="white" mb="sm" fz={{ base: 30, md: 36 }}>
            {t('features.cta.title')}
          </Title>
          <Text c="indigo.1" size="lg" mb="xl" maw={640} mx="auto">
            {t('features.cta.lead')}
          </Text>
          <Button
            component={Link}
            to="/rejestracja"
            size="xl"
            radius="md"
            rightSection={<ArrowRight size={20} weight="bold" />}
            className="cta-white-btn"
          >
            {t('features.cta.button')}
          </Button>
        </Container>
      </section>

      <Footer />

      {/* Feature detail modal */}
      <FeatureModal feature={activeFeature} onClose={closeFeature} />
    </div>
  );
}
